// PR-3634 contracts v4 §6.3: the "Free Trial" badge, the days-left line and the collapsed-menu dot.
// Each renders nothing unless the account is on a Pro trial, so non-trial accounts see today's UI.
import React from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Badge } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { getProTrialDaysRemainingText, selectIsOnProTrial, selectProTrialDaysRemaining } from '../../redux/reducers/ProTrialSlice';

type ProTrialVariant = 'sidebar' | 'card';

const useStyles = makeStyles(() => ({
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 20,
    fontWeight: 700,
    whiteSpace: 'nowrap',
    lineHeight: 1.5,
    flexShrink: 0,
  },
  sidebarBadge: {
    // the card header spreads "Your plan" and this badge apart (space-between) with no minimum gap,
    // so in the narrow side menu they touched; keep a fixed gap on the inline-start side (RTL-safe)
    marginInlineStart: 8,
    padding: '1px 8px',
    fontSize: '0.62rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    backgroundColor: '#FFD23F',
    color: '#3D2E00',
  },
  cardBadge: {
    padding: '6px 14px',
    fontSize: '14px',
    backgroundColor: 'rgba(255, 193, 7, 0.2)',
    color: 'var(--pls-d-text, #7A5A00)',
    border: '1px solid rgba(255, 193, 7, 0.6)',
  },
  sidebarDays: {
    marginTop: 6,
    fontSize: '0.72rem',
    fontWeight: 500,
    color: 'rgba(255, 255, 255, 0.85)',
  },
  cardDays: {
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--pls-d-text-2, #666)',
  },
  dot: {
    backgroundColor: '#FFD23F',
  },
}));

export const ProTrialBadge = ({ variant = 'sidebar' }: { variant?: ProTrialVariant }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const isOnProTrial = useSelector(selectIsOnProTrial);
  if (!isOnProTrial) return null;
  return (
    <span className={clsx(classes.badge, variant === 'card' ? classes.cardBadge : classes.sidebarBadge)}>
      {t('billing.tier.proTrial.badge')}
    </span>
  );
};

export const ProTrialDaysLeft = ({ variant = 'sidebar' }: { variant?: ProTrialVariant }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const isOnProTrial = useSelector(selectIsOnProTrial);
  const daysRemaining = useSelector(selectProTrialDaysRemaining);
  if (!isOnProTrial) return null;
  return (
    <div className={variant === 'card' ? classes.cardDays : classes.sidebarDays}>
      {getProTrialDaysRemainingText(t, daysRemaining)}
    </div>
  );
};

// Collapsed side menu: a dot on the plan icon while on trial; otherwise the icon unchanged.
export const ProTrialDot = ({ children }: { children: React.ReactElement }) => {
  const classes = useStyles();
  const isOnProTrial = useSelector(selectIsOnProTrial);
  if (!isOnProTrial) return children;
  return (
    <Badge variant="dot" overlap="circle" classes={{ dot: classes.dot }}>
      {children}
    </Badge>
  );
};
